/**
 * TeamDetailPage - Team detail view at /company/:slug/teams/:teamId
 *
 * Displays team header, radar chart with member overlays, axis balance
 * analysis, member list, and gap analysis. BtoB-oriented language only
 * (no fortune-telling or astrology references).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled, { keyframes, useTheme } from 'styled-components';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import companyService from '../../services/companyService';
import RadarChart from '../../components/company/RadarChart';
import MemberCard from '../../components/company/MemberCard';
import SpectrumBar from '../../components/company/SpectrumBar';
import StatCard from '../../components/company/StatCard';

/* ================================================================== */
/*  Constants                                                          */
/* ================================================================== */

const AXIS_KEYS = ['activation', 'judgment', 'choice', 'resonance', 'awareness'];

const OVERLAY_COLORS = [
  '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#A855F7',
  '#EF4444', '#14B8A6', '#F97316', '#6366F1', '#84CC16',
];

/* ================================================================== */
/*  Keyframes                                                          */
/* ================================================================== */

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ================================================================== */
/*  Styled components                                                  */
/* ================================================================== */

/* ---- Page shell ---- */

const PageContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 48px 24px 80px;
  animation: ${fadeIn} 0.35s ease-out;

  @media (max-width: ${(props) => props.theme.breakpoints?.mobile || '640px'}) {
    padding: 24px 16px 64px;
  }
`;

/* ---- Breadcrumb ---- */

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 24px;
  font-size: 0.82rem;
  color: ${(props) => props.theme.colors.textSecondary};
`;

const BreadcrumbLink = styled(Link)`
  color: ${(props) => props.theme.colors.textSecondary};
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: ${(props) => props.theme.colors.primary};
  }
`;

const BreadcrumbSep = styled.span`
  color: ${(props) => props.theme.colors.border};
  user-select: none;
`;

const BreadcrumbCurrent = styled.span`
  color: ${(props) => props.theme.colors.text};
  font-weight: 600;
`;

/* ---- Team header ---- */

const TeamHeader = styled.div`
  margin-bottom: 32px;
`;

const TeamTitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 16px;
  margin-bottom: 8px;
`;

const TeamName = styled.h1`
  font-size: 1.8rem;
  font-weight: 800;
  color: ${(props) => props.theme.colors.text};
  margin: 0;
  font-family: ${(props) => props.theme.fonts?.main};
  line-height: 1.2;

  @media (max-width: ${(props) => props.theme.breakpoints?.mobile || '640px'}) {
    font-size: 1.4rem;
  }
`;

const TypeBadge = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 4px 14px;
  border-radius: ${(props) => props.theme.borderRadius?.full || '9999px'};
  background: ${(props) => props.theme.colors.primaryGlow || 'rgba(99,102,241,0.12)'};
  border: 1px solid ${(props) => props.theme.colors.primary};
  font-family: ${(props) => props.theme.fonts?.mono};
  font-size: 0.8rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.primary};
  text-decoration: none;
  &:hover { opacity: 0.8; }
`;

const MemberCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 14px;
  border-radius: ${(props) => props.theme.borderRadius?.full || '9999px'};
  background: ${(props) => props.theme.colors.bgTertiary};
  border: 1px solid ${(props) => props.theme.colors.border};
  font-size: 0.8rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.textSecondary};
`;

const TeamSubtitle = styled.p`
  font-size: 0.9rem;
  color: ${(props) => props.theme.colors.textSecondary};
  margin: 4px 0 0 0;
  line-height: 1.5;
`;

const TeamTypeDescription = styled.p`
  font-size: 0.95rem;
  color: ${(props) => props.theme.colors.text};
  margin: 12px 0 0 0;
  line-height: 1.5;
  padding: 12px 16px;
  background: ${(props) => props.theme.colors.bgSecondary};
  border-left: 3px solid ${(props) => props.theme.colors.primary};
  border-radius: 0 ${(props) => props.theme.borderRadius?.md || '8px'} ${(props) => props.theme.borderRadius?.md || '8px'} 0;
`;

/* ---- Stats grid ---- */

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 32px;

  @media (max-width: ${(props) => props.theme.breakpoints?.tablet || '768px'}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${(props) => props.theme.breakpoints?.mobile || '640px'}) {
    grid-template-columns: 1fr;
  }
`;

/* ---- Two-column visualization grid ---- */

const VisualizationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 40px;

  @media (max-width: ${(props) => props.theme.breakpoints?.tablet || '768px'}) {
    grid-template-columns: 1fr;
  }
`;

const ChartPanel = styled.div`
  background: ${(props) => props.theme.colors.bgSecondary};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius?.lg || '12px'};
  padding: 24px;
`;

const PanelTitle = styled.h2`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 20px 0;
`;

const ChartCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* ---- Members section ---- */

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
  margin: 0;
`;

const CountChip = styled.span`
  font-size: 0.78rem;
  font-family: ${(props) => props.theme.fonts?.mono};
  font-weight: 600;
  color: ${(props) => props.theme.colors.primary};
  background: ${(props) => props.theme.colors.primaryGlow || 'rgba(99,102,241,0.12)'};
  padding: 2px 10px;
  border-radius: ${(props) => props.theme.borderRadius?.full || '9999px'};
`;

const MembersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 40px;

  @media (max-width: ${(props) => props.theme.breakpoints?.tablet || '768px'}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${(props) => props.theme.breakpoints?.mobile || '640px'}) {
    grid-template-columns: 1fr;
  }
`;

/* ---- Gap Analysis section ---- */

const GapSection = styled.div`
  margin-bottom: 40px;
`;

const GapGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: ${(props) => props.theme.breakpoints?.mobile || '640px'}) {
    grid-template-columns: 1fr;
  }
`;

const GapPanel = styled.div`
  background: ${(props) => props.theme.colors.bgSecondary};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius?.lg || '12px'};
  padding: 20px;
`;

const GapPanelTitle = styled.h3`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 14px 0;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const GapTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  font-size: 0.82rem;
  font-weight: 600;
  background: ${(props) => props.$bg};
  color: ${(props) => props.$color};
  border: 1px solid ${(props) => props.$borderColor || 'transparent'};
`;

const GapEmpty = styled.p`
  font-size: 0.85rem;
  color: ${(props) => props.theme.colors.textSecondary};
  margin: 0;
  font-style: italic;
`;

/* ---- AI Analysis ---- */

const AiSection = styled.div`
  margin: 32px 0;
`;

const AiButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  background: linear-gradient(135deg, ${(props) => props.theme.colors.primary}, ${(props) => props.theme.colors.secondary || '#7C3AED'});
  color: #fff;
  border: none;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  margin-bottom: 20px;
  &:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const AiResultGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  @media (max-width: ${(props) => props.theme.breakpoints?.tablet || '768px'}) { grid-template-columns: 1fr; }
`;

const AiCard = styled.div`
  background: ${(props) => props.theme.colors.bgSecondary};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius?.lg || '12px'};
  padding: 20px;
`;

const AiCardTitle = styled.h3`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${(props) => props.$color || props.theme.colors.primary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
`;

const AiList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const AiListItem = styled.li`
  font-size: 0.85rem;
  line-height: 1.6;
  color: ${(props) => props.theme.colors.text};
  padding: 6px 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.border}20;
  &:last-child { border-bottom: none; }
`;

const AiSummary = styled.div`
  grid-column: 1 / -1;
  background: ${(props) => props.theme.colors.bgSecondary};
  border: 1px solid ${(props) => props.theme.colors.primary}40;
  border-radius: ${(props) => props.theme.borderRadius?.lg || '12px'};
  padding: 20px;
  font-size: 0.9rem;
  line-height: 1.7;
  color: ${(props) => props.theme.colors.text};
`;

const aiPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const AiLoadingText = styled.span`
  animation: ${aiPulse} 1.5s ease-in-out infinite;
`;

/* ---- Empty / Error / Loading ---- */

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${(props) => props.theme.colors.textSecondary};
  background: ${(props) => props.theme.colors.bgSecondary};
  border: 1px dashed ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius?.lg || '12px'};
  font-size: 0.9rem;
  line-height: 1.6;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 64px 24px;
`;

const ErrorTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
  margin: 0 0 12px 0;
`;

const ErrorMessage = styled.p`
  font-size: 0.9rem;
  color: ${(props) => props.theme.colors.textSecondary};
  margin: 0 0 24px 0;
`;

const RetryButton = styled.button`
  padding: 10px 24px;
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  background: ${(props) => props.theme.colors.primary};
  color: #fff;
  border: none;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${(props) => props.theme.colors.secondary};
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${(props) => props.theme.colors.textSecondary};
  text-decoration: none;
  font-size: 0.85rem;
  margin-top: 16px;
  transition: color 0.2s;

  &:hover {
    color: ${(props) => props.theme.colors.primary};
  }
`;

/* ---- Loading skeleton ---- */

const SkeletonBlock = styled.div`
  background: linear-gradient(
    90deg,
    ${(props) => props.theme.colors.bgTertiary} 25%,
    ${(props) => props.theme.colors.bgSecondary} 50%,
    ${(props) => props.theme.colors.bgTertiary} 75%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s infinite;
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  height: ${(props) => props.$h || '20px'};
  width: ${(props) => props.$w || '100%'};
  margin-bottom: ${(props) => props.$mb || '0'};
`;

const SkeletonCard = styled.div`
  background: ${(props) => props.theme.colors.bgTertiary};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  padding: 24px;
`;

/* ================================================================== */
/*  SVG Icons (inline)                                                 */
/* ================================================================== */

const ChevronRightIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const UsersIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

/* ================================================================== */
/*  Helper: axis name map                                              */
/* ================================================================== */

function useAxisNames() {
  const { t } = useLanguage();
  return {
    activation: t('行動特性', 'Behavioral'),
    judgment: t('判断基準', 'Decision'),
    choice: t('価値観', 'Values'),
    resonance: t('共感性', 'Empathy'),
    awareness: t('自己認識', 'Self-Aware'),
  };
}

/* ================================================================== */
/*  Loading skeleton sub-component                                     */
/* ================================================================== */

function LoadingSkeleton() {
  return (
    <PageContainer>
      {/* Breadcrumb skeleton */}
      <SkeletonBlock $h="16px" $w="240px" $mb="24px" />

      {/* Header skeleton */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
          <SkeletonBlock $h="32px" $w="200px" />
          <SkeletonBlock $h="26px" $w="70px" />
          <SkeletonBlock $h="26px" $w="100px" />
        </div>
        <SkeletonBlock $h="16px" $w="300px" $mb="12px" />
        <SkeletonBlock $h="48px" $w="100%" />
      </div>

      {/* Visualization grid skeleton */}
      <VisualizationGrid>
        <SkeletonCard style={{ minHeight: 320 }}>
          <SkeletonBlock $h="14px" $w="50%" $mb="20px" />
          <SkeletonBlock $h="240px" $w="240px" style={{ margin: '0 auto', borderRadius: '50%' }} />
        </SkeletonCard>
        <SkeletonCard style={{ minHeight: 320 }}>
          <SkeletonBlock $h="14px" $w="50%" $mb="20px" />
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} style={{ marginBottom: 16 }}>
              <SkeletonBlock $h="14px" $w="40%" $mb="6px" />
              <SkeletonBlock $h="10px" $w="100%" />
            </div>
          ))}
        </SkeletonCard>
      </VisualizationGrid>

      {/* Members skeleton */}
      <SkeletonBlock $h="24px" $w="160px" $mb="20px" />
      <MembersGrid>
        {[0, 1, 2].map((i) => (
          <SkeletonCard key={i}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
              <SkeletonBlock $h="42px" $w="42px" style={{ borderRadius: '50%', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <SkeletonBlock $h="16px" $w="70%" $mb="6px" />
                <SkeletonBlock $h="12px" $w="50%" />
              </div>
            </div>
            {[0, 1, 2, 3, 4].map((j) => (
              <SkeletonBlock key={j} $h="8px" $w="100%" $mb="6px" />
            ))}
          </SkeletonCard>
        ))}
      </MembersGrid>
    </PageContainer>
  );
}

/* ================================================================== */
/*  Main component                                                     */
/* ================================================================== */

function TeamDetailPage() {
  const { slug, teamId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { isAuthenticated } = useAuth();
  const theme = useTheme();

  /* ---- State ---- */
  const [company, setCompany] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiCachedAt, setAiCachedAt] = useState(null);

  /* ---- Axis names (translated) ---- */
  const axisNames = useAxisNames();

  /* ---- Data fetching ---- */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [companyData, analysisData, cachedAi] = await Promise.all([
        companyService.getCompany(slug),
        companyService.getTeamAnalysis(slug, teamId).catch(() => null),
        companyService.getCachedTeamAiAnalysis(slug, teamId).catch(() => null),
      ]);
      setCompany(companyData);
      setAnalysis(analysisData);
      if (cachedAi && cachedAi.cached && cachedAi.result) {
        setAiResult(cachedAi.result);
        setAiCachedAt(cachedAi.created_at);
      }
    } catch (err) {
      setError(err.message || t('データの取得に失敗しました', 'Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  }, [slug, teamId, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const runAiAnalysis = useCallback(async () => {
    setAiLoading(true);
    setAiError(null);
    try {
      const result = await companyService.getTeamAiAnalysis(slug, teamId);
      setAiResult(result);
      setAiCachedAt(new Date().toISOString());
    } catch (err) {
      setAiError(err.message || t('AI分析に失敗しました', 'AI analysis failed'));
    } finally {
      setAiLoading(false);
    }
  }, [slug, teamId, t]);

  /* ---- Derived: find team from company data ---- */
  const team = useMemo(() => {
    if (!company?.departments) return null;
    for (const dept of company.departments) {
      if (dept.teams) {
        const found = dept.teams.find(
          (tm) => String(tm.id) === String(teamId)
        );
        if (found) return { ...found, department_name: dept.name };
      }
    }
    // Also check top-level teams array if present
    if (company.teams) {
      const found = company.teams.find(
        (tm) => String(tm.id) === String(teamId)
      );
      if (found) return found;
    }
    return null;
  }, [company, teamId]);

  /* ---- Derived: find team members ---- */
  const teamMembers = useMemo(() => {
    if (!company?.members) return [];
    return company.members.filter(
      (m) => String(m.team_id) === String(teamId)
    );
  }, [company, teamId]);

  /* ---- Radar chart: main centroid data ---- */
  const radarData = useMemo(() => {
    if (!analysis?.centroid) return [];
    return AXIS_KEYS.map((key) => ({
      label: axisNames[key],
      value: Math.min(Math.max(analysis.centroid[key] ?? 0, 0), 1),
    }));
  }, [analysis, axisNames]);

  /* ---- Radar chart: member overlays (show only if <10 members) ---- */
  const memberOverlays = useMemo(() => {
    if (teamMembers.length === 0 || teamMembers.length >= 10) return [];
    return teamMembers
      .filter((m) => m.axes)
      .map((m, idx) => {
        const needsNormalize = Object.values(m.axes).some(
          (v) => typeof v === 'number' && v <= 1
        );
        return {
          data: AXIS_KEYS.map((key) => {
            const raw = m.axes[key] ?? 0;
            const norm = needsNormalize ? Math.min(Math.max(raw, 0), 1) : raw / 1000;
            return { label: axisNames[key], value: norm };
          }),
          color: OVERLAY_COLORS[idx % OVERLAY_COLORS.length],
          opacity: 0.15,
        };
      });
  }, [teamMembers, axisNames]);

  /* ---- Gap analysis data ---- */
  const gapData = useMemo(() => {
    if (!analysis) return null;
    const missing = analysis.missing_categories || analysis.gap?.missing_categories || null;
    const risk = analysis.risk_categories || analysis.gap?.risk_categories || null;
    if (!missing && !risk) return null;
    return { missing: missing || [], risk: risk || [] };
  }, [analysis]);

  /* ================================================================ */
  /*  Render states                                                    */
  /* ================================================================ */

  if (!isAuthenticated) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorTitle>{t('ログインが必要です', 'Login Required')}</ErrorTitle>
          <ErrorMessage>{t('このページを利用するにはログインまたは新規登録が必要です。', 'Please log in or sign up to access this page.')}</ErrorMessage>
          <RetryButton onClick={() => navigate('/')}>
            {t('ログイン / 新規登録', 'Login / Sign Up')}
          </RetryButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    const isAuthError = error === 'Not authenticated' || error === 'Invalid or expired token';
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorTitle>{isAuthError ? t('ログインが必要です', 'Login Required') : t('エラーが発生しました', 'An Error Occurred')}</ErrorTitle>
          <ErrorMessage>{isAuthError ? t('このページを利用するにはログインまたは新規登録が必要です。', 'Please log in or sign up to access this page.') : error}</ErrorMessage>
          <RetryButton onClick={isAuthError ? () => navigate('/') : fetchData}>
            {isAuthError ? t('ログイン / 新規登録', 'Login / Sign Up') : t('再試行', 'Retry')}
          </RetryButton>
          {!isAuthError && (
            <>
              <br />
              <BackLink to={`/company/${slug}`}>
                <ArrowLeftIcon />
                {t('ダッシュボードに戻る', 'Back to Dashboard')}
              </BackLink>
            </>
          )}
        </ErrorContainer>
      </PageContainer>
    );
  }

  if (!company) {
    return (
      <PageContainer>
        <EmptyState>
          {t('組織が見つかりませんでした。', 'Organization not found.')}
        </EmptyState>
        <BackLink to={`/company/${slug}`}>
          <ArrowLeftIcon />
          {t('ダッシュボードに戻る', 'Back to Dashboard')}
        </BackLink>
      </PageContainer>
    );
  }

  if (!team) {
    return (
      <PageContainer>
        <Breadcrumb>
          <BreadcrumbLink to={`/company/${slug}`}>{company.name}</BreadcrumbLink>
          <BreadcrumbSep><ChevronRightIcon /></BreadcrumbSep>
          <BreadcrumbCurrent>--</BreadcrumbCurrent>
        </Breadcrumb>
        <EmptyState>
          {t('チームが見つかりませんでした。', 'Team not found.')}
        </EmptyState>
        <BackLink to={`/company/${slug}`}>
          <ArrowLeftIcon />
          {t('ダッシュボードに戻る', 'Back to Dashboard')}
        </BackLink>
      </PageContainer>
    );
  }

  /* ================================================================ */
  /*  Main render                                                      */
  /* ================================================================ */

  const typeCode = analysis?.nearest_type?.type_code || null;
  const memberCount = teamMembers.length;
  const diagnosedCount = teamMembers.filter(m => m.type_code).length;

  const strongestAxis = analysis?.axis_balance?.strongest_axis
    ? axisNames[analysis.axis_balance.strongest_axis] || analysis.axis_balance.strongest_axis
    : '--';

  const weakestAxis = analysis?.axis_balance?.weakest_axis
    ? axisNames[analysis.axis_balance.weakest_axis] || analysis.axis_balance.weakest_axis
    : '--';

  return (
    <PageContainer>
      {/* ---- Breadcrumb ---- */}
      <Breadcrumb>
        <BreadcrumbLink to={`/company/${slug}`}>{company.name}</BreadcrumbLink>
        <BreadcrumbSep><ChevronRightIcon /></BreadcrumbSep>
        <BreadcrumbCurrent>{team.name}</BreadcrumbCurrent>
      </Breadcrumb>

      {/* ---- Team Header ---- */}
      <TeamHeader>
        <TeamTitleRow>
          <TeamName>{team.name}</TeamName>
          {typeCode && <TypeBadge to={`/types/${typeCode}`}>{typeCode}</TypeBadge>}
          <MemberCountBadge>
            <UsersIcon />
            {memberCount} {t('名', memberCount === 1 ? 'member' : 'members')}
          </MemberCountBadge>
        </TeamTitleRow>

        {team.department_name && (
          <TeamSubtitle>
            {t('所属部署', 'Department')}: {team.department_name}
          </TeamSubtitle>
        )}

        {typeCode && (
          <TeamTypeDescription>
            {t(
              `このチームは ${typeCode} 型チームです`,
              `This team is a ${typeCode}-type team`
            )}
          </TeamTypeDescription>
        )}
      </TeamHeader>

      {/* ---- Stats Cards ---- */}
      <StatsGrid>
        <StatCard
          title={t('メンバー', 'Members')}
          value={`${diagnosedCount} / ${memberCount}`}
          subtitle={t('行動特性分析 完了数', 'Behavioral analysis completed')}
        />
        <StatCard
          title={t('チームタイプ', 'Team Type')}
          value={typeCode || '--'}
          subtitle={t('統計的分析に基づく分類', 'Based on statistical analysis')}
        />
        <StatCard
          title={t('強み', 'Strength')}
          value={strongestAxis}
          subtitle={t('チームの優位特性', 'Dominant team trait')}
        />
        <StatCard
          title={t('弱み', 'Weakness')}
          value={weakestAxis}
          subtitle={t('成長ポテンシャル', 'Growth potential')}
        />
      </StatsGrid>

      {/* ---- Two-column Visualization Grid ---- */}
      <VisualizationGrid>
        {/* Left: Radar Chart */}
        <ChartPanel>
          <PanelTitle>
            {t('チームレーダーチャート', 'Team Radar Chart')}
          </PanelTitle>
          {radarData.length === 5 ? (
            <ChartCenter>
              <RadarChart
                data={radarData}
                size={280}
                overlays={memberOverlays}
              />
            </ChartCenter>
          ) : (
            <EmptyState>
              {t(
                '分析データがまだありません。メンバーの行動特性分析が完了すると表示されます。',
                'No analysis data yet. This will be displayed once members complete their behavioral analysis.'
              )}
            </EmptyState>
          )}
          {memberOverlays.length > 0 && (
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <span style={{
                fontSize: '0.72rem',
                color: theme.colors.textSecondary,
              }}>
                {t(
                  '半透明のラインは各メンバーの個別プロファイルを示しています',
                  'Translucent lines represent individual member profiles'
                )}
              </span>
            </div>
          )}
        </ChartPanel>

        {/* Right: Structure Spectrum */}
        <ChartPanel>
          <PanelTitle>
            {t('構造スペクトラム', 'Structure Spectrum')}
          </PanelTitle>
          <SpectrumBar
            axes={analysis?.centroid || {}}
            normalize
            lang={lang}
          />
        </ChartPanel>
      </VisualizationGrid>

      {/* ---- AI Analysis (above Members) ---- */}
      <AiSection>
        <SectionHeader>
          <SectionTitle>{t('AI組織分析', 'AI Analysis')}</SectionTitle>
          {aiCachedAt && (
            <span style={{ fontSize: '0.75rem', color: theme.colors.textSecondary }}>
              {new Date(aiCachedAt).toLocaleDateString()}
            </span>
          )}
        </SectionHeader>

        {!aiResult && !aiLoading && (
          <AiButton onClick={runAiAnalysis} disabled={aiLoading || memberCount < 2}>
            {memberCount < 2
              ? t('メンバー2名以上で分析可能', 'Requires 2+ members')
              : t('AIでこのチームを分析する', 'Analyze this team with AI')}
          </AiButton>
        )}

        {!aiResult && aiLoading && (
          <AiButton disabled>
            <AiLoadingText>{t('AI分析中...', 'Analyzing...')}</AiLoadingText>
          </AiButton>
        )}

        {aiError && (
          <p style={{ color: theme.colors.error || '#EF4444', fontSize: '0.85rem' }}>{aiError}</p>
        )}

        {aiResult && (
          <div style={{ position: 'relative' }}>
            {aiLoading && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.6)', borderRadius: '12px', zIndex: 10,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px',
              }}>
                <div style={{ width: '32px', height: '32px', border: '3px solid rgba(255,255,255,0.2)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                <span style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 600 }}>
                  {t('再分析中...', 'Re-analyzing...')}
                </span>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}
            <AiResultGrid>
              {aiResult.overview && (
                <AiSummary>{aiResult.overview}</AiSummary>
              )}
              {aiResult.structural_strengths?.length > 0 && (
                <AiCard>
                  <AiCardTitle $color="#10B981">{t('構造的な強み', 'Structural Strengths')}</AiCardTitle>
                  <AiList>{aiResult.structural_strengths.map((s, i) => <AiListItem key={i}>{s}</AiListItem>)}</AiList>
                </AiCard>
              )}
              {aiResult.structural_risks?.length > 0 && (
                <AiCard>
                  <AiCardTitle $color={theme.colors.accent || '#EC4899'}>{t('構造的なリスク', 'Structural Risks')}</AiCardTitle>
                  <AiList>{aiResult.structural_risks.map((s, i) => <AiListItem key={i}>{s}</AiListItem>)}</AiList>
                </AiCard>
              )}
              {aiResult.position_fit?.length > 0 && (
                <AiCard>
                  <AiCardTitle $color="#F59E0B">{t('配置適性', 'Position Fit')}</AiCardTitle>
                  <AiList>{aiResult.position_fit.map((s, i) => <AiListItem key={i}>{s}</AiListItem>)}</AiList>
                </AiCard>
              )}
              {aiResult.team_composition_advice?.length > 0 && (
                <AiCard>
                  <AiCardTitle $color="#3B82F6">{t('チーム構成アドバイス', 'Team Composition')}</AiCardTitle>
                  <AiList>{aiResult.team_composition_advice.map((s, i) => <AiListItem key={i}>{s}</AiListItem>)}</AiList>
                </AiCard>
              )}
              {aiResult.missing_capabilities?.length > 0 && (
                <AiCard style={{ gridColumn: '1 / -1' }}>
                  <AiCardTitle $color="#8B5CF6">{t('補強が望まれる人材像', 'Missing Capabilities')}</AiCardTitle>
                  <AiList>{aiResult.missing_capabilities.map((s, i) => <AiListItem key={i}>{s}</AiListItem>)}</AiList>
                </AiCard>
              )}
              {aiResult.action_items?.length > 0 && (
                <AiCard style={{ gridColumn: '1 / -1' }}>
                  <AiCardTitle>{t('アクションの方向性', 'Action Items')}</AiCardTitle>
                  <AiList>{aiResult.action_items.map((s, i) => <AiListItem key={i}>{s}</AiListItem>)}</AiList>
                </AiCard>
              )}
            </AiResultGrid>
            <div style={{ marginTop: '12px', textAlign: 'right' }}>
              <AiButton onClick={runAiAnalysis} disabled={aiLoading} style={{ fontSize: '0.8rem', padding: '8px 16px' }}>
                {t('再分析', 'Re-analyze')}
              </AiButton>
            </div>
          </div>
        )}
      </AiSection>

      {/* ---- Member List ---- */}
      <SectionHeader>
        <SectionTitle>{t('メンバー', 'Members')}</SectionTitle>
        <CountChip>{memberCount}</CountChip>
      </SectionHeader>

      {memberCount > 0 ? (
        <MembersGrid>
          {teamMembers.map((member) => (
            <MemberCard
              key={member.id}
              member={member}
              onClick={() => navigate(`/company/${slug}/members/${member.id}`)}
            />
          ))}
        </MembersGrid>
      ) : (
        <EmptyState style={{ marginBottom: 40 }}>
          {t(
            'このチームにはまだメンバーがいません。ダッシュボードからメンバーをチームに割り当ててください。',
            'No members in this team yet. Assign members to this team from the dashboard.'
          )}
        </EmptyState>
      )}

      {/* ---- Gap Analysis Section ---- */}
      {gapData && (
        <GapSection>
          <SectionHeader>
            <SectionTitle>{t('ギャップ分析', 'Gap Analysis')}</SectionTitle>
          </SectionHeader>

          <GapGrid>
            {/* Missing Categories */}
            <GapPanel>
              <GapPanelTitle>
                {t('不足カテゴリ', 'Missing Categories')}
              </GapPanelTitle>
              {gapData.missing.length > 0 ? (
                <TagList>
                  {gapData.missing.map((cat) => (
                    <GapTag
                      key={cat}
                      $bg={`${theme.colors.accent || '#EC4899'}15`}
                      $color={theme.colors.accent || '#EC4899'}
                      $borderColor={`${theme.colors.accent || '#EC4899'}40`}
                    >
                      {cat}
                    </GapTag>
                  ))}
                </TagList>
              ) : (
                <GapEmpty>
                  {t(
                    '不足しているカテゴリはありません。',
                    'No missing categories detected.'
                  )}
                </GapEmpty>
              )}
            </GapPanel>

            {/* Risk Categories (single member) */}
            <GapPanel>
              <GapPanelTitle>
                {t('リスクカテゴリ (1名のみ)', 'Risk Categories (Single Member)')}
              </GapPanelTitle>
              {gapData.risk.length > 0 ? (
                <TagList>
                  {gapData.risk.map((cat) => (
                    <GapTag
                      key={cat}
                      $bg={`${theme.colors.warning || '#F59E0B'}15`}
                      $color={theme.colors.warning || '#F59E0B'}
                      $borderColor={`${theme.colors.warning || '#F59E0B'}40`}
                    >
                      {cat}
                    </GapTag>
                  ))}
                </TagList>
              ) : (
                <GapEmpty>
                  {t(
                    'リスクカテゴリはありません。',
                    'No risk categories detected.'
                  )}
                </GapEmpty>
              )}
            </GapPanel>
          </GapGrid>
        </GapSection>
      )}

      {/* ---- Back link ---- */}
      <BackLink to={`/company/${slug}`}>
        <ArrowLeftIcon />
        {t('ダッシュボードに戻る', 'Back to Dashboard')}
      </BackLink>
    </PageContainer>
  );
}

export default TeamDetailPage;
